/* makePointLaps.ts */

import { Md5 } from "ts-md5";
import { RaceData } from "./types";

export const makePointLaps = (
  sprints: number,
  laps: number,
  seed: string,
  iteration = 0,
): RaceData => {
  // unique string for this race
  const race = `${seed.toLocaleLowerCase().trim()}${laps}${sprints}`;

  let hashSeed = race;
  let hash = "";

  // make a hash with 32 chars per sprint in the race
  for (let i = 0; i < sprints; i++) {
    hashSeed = Md5.hashStr(hashSeed);
    hash += hashSeed;
  }

  // array of 2-char 0-f hex values, 16 for each lap of the race
  const hexPairs = hash.match(/.{1,2}/g);
  if (!hexPairs)
    return {
      pointLaps: [],
      bonusIdx: 0,
      missingSprints: true,
    };

  // convert to decimal integers between 0 and 255
  const integers = hexPairs
    .map((hexVal) => parseInt(hexVal, 16))
    .filter((int) => !isNaN(int));

  // normalize [0,255] => [0,laps]
  const lapValues = integers.map((val) =>
    Math.floor((val * laps) / 16 ** 2),
  );

  // pull an odd-numbered index from the original
  // hash, and use it to create a random number
  // between 0 and the number of sprints
  const bonusDecimal =
    parseInt(hash.substring(1, 4), 16) / 16 ** 3;
  const bonusIdx = Math.floor(
    bonusDecimal * Math.min(laps, sprints),
  );

  if (sprints >= laps)
    // ugh, every lap is a sprint, return the lot
    return {
      pointLaps: new Array(laps)
        .fill(undefined)
        .map((_v, idx) => laps - 1 - idx),
      bonusIdx,
      missingSprints: false,
    };

  let idx = 0;
  let unsortedLaps: number[] = [];

  while (
    unsortedLaps.length < sprints &&
    idx < lapValues.length
  ) {
    let provisionalSprint = lapValues[idx];
    if (!unsortedLaps.includes(provisionalSprint))
      unsortedLaps.push(provisionalSprint);
    idx++;
  }

  // did we get VERY unlucky with our hash? it's
  // possible that even with 16 pairs per sprint,
  // we weren't able to dedupe successfully and
  // make a clean race. try again; if we try 10
  // times and still nothing, you get fewer sprints.

  // odds of missing once in a typical race scenario
  // are insanely small, atoms-in-the-known-universe
  // small. edge case races bring those odds down to
  // roughly the odds of winning powerball. Either
  // way, we'll let you know.

  const missingSprints = unsortedLaps.length < sprints;

  if (unsortedLaps.length < sprints && iteration < 10)
    return makePointLaps(
      sprints,
      laps,
      hash,
      iteration + 1,
    );

  // decreasing order
  const pointLaps = unsortedLaps.sort((a, b) => b - a);

  return { pointLaps, bonusIdx, missingSprints };
};
